import type { ICellEditorParams } from '@ag-grid-community/core';
import type { GridOptions, GridRow } from '../grid.types';

export interface GridRefreshableCellEditor {
    hasBeenEdited?: boolean;
    params?: ICellEditorParams;
    syncValueFromRow(value: unknown): void | Promise<void>;
}

interface AgGridInputCellEditor {
    cellEditorInput?: {
        params?: ICellEditorParams;
        eInput?: {
            setValue?: (value: unknown) => void;
            eInput?: {
                select?: () => void;
            };
        };
    };
}

export async function updateActiveCellEditorFromGridRow<TType extends GridRow = GridRow>(options: GridOptions<TType>): Promise<void> {
    const gridApi = options.gridfunc?.getGridApi?.();
    const focusedCell = gridApi?.getFocusedCell();

    if (focusedCell?.rowIndex === undefined || focusedCell.rowIndex === null) return;

    const rowNode = options.gridfunc?.getRowAt(focusedCell.rowIndex);
    const row = rowNode?.data;
    const activeEditor = gridApi?.getCellEditorInstances()?.[0];

    if (!rowNode || !row || !activeEditor) return;

    if (isRefreshableCellEditor(activeEditor)) {
        await syncRefreshableCellEditor(activeEditor, row);
        return;
    }

    syncAgGridInputCellEditor(activeEditor, row);
}

function isRefreshableCellEditor(editor: unknown): editor is GridRefreshableCellEditor {
    return typeof editor === 'object' && editor !== null && typeof (editor as GridRefreshableCellEditor).syncValueFromRow === 'function';
}

async function syncRefreshableCellEditor(editor: GridRefreshableCellEditor, row: GridRow): Promise<void> {
    if (editor.hasBeenEdited === true) return;

    const colId = editor.params?.column?.getColId();

    if (!colId) return;

    await editor.syncValueFromRow(row[colId]);
}

function syncAgGridInputCellEditor(editor: unknown, row: GridRow): void {
    const activeEditor = editor as AgGridInputCellEditor;
    const params = activeEditor.cellEditorInput?.params;
    const colId = params?.column?.getColId();
    const input = activeEditor.cellEditorInput?.eInput;

    if (!colId || typeof input?.setValue !== 'function') return;

    input.setValue(row[colId]);
    setTimeout(() => input.eInput?.select?.(), 1);
}
